import { registerGlobal, getRegisteredGlobal } from "@trumpf/ux.utils";
import { NotificationProviderScope } from "./interface";
import { debounce } from "../../utils/debounce";
const NAMESPACE = "trumpf.ux.notificationProvider";
let subscriberIncrement = 0;
const createSubscriberId = () => `notification-subscriber-${++subscriberIncrement}`;
const sortNotifications = (a, b) => b.timestamp - a.timestamp;
export class NotificationProvider {
    constructor() {
        this.state = {
            counter: 0,
            error: null,
            loadMore: null,
            notifications: [],
            pending: false
        };
        this.subscribers = {};
        // ------------------------------------------------------------------------
        // Notification Backends
        // ------------------------------------------------------------------------
        this.registeredBackends = {};
        this.callBackendLoadMore = () => {
            Object.values(this.registeredBackends).forEach(({ scope }) => {
                const scopedNotificationProvider = getNotificationProvider(scope);
                if (typeof scopedNotificationProvider.loadMore === "function") {
                    scopedNotificationProvider.loadMore();
                }
            });
        };
        this.handleBackendProviderState = () => {
            let backendPending = false;
            let backendError = null;
            let backendNotifications = [];
            let backendTotalCounter = 0;
            let backendLoadMoreCounter = 0;
            Object.values(this.registeredBackends).forEach(({ scope }) => {
                const scopedNotificationProvider = getNotificationProvider(scope);
                if (scopedNotificationProvider.pending) {
                    backendPending = true;
                }
                if (scopedNotificationProvider.error !== null && backendError === null) {
                    backendError = scopedNotificationProvider.error;
                }
                if (typeof scopedNotificationProvider.loadMore === "function") {
                    backendLoadMoreCounter++;
                }
                backendNotifications = [...backendNotifications, ...scopedNotificationProvider.notifications];
                backendTotalCounter += Math.max(scopedNotificationProvider.notifications.length, scopedNotificationProvider.counter);
            });
            this.setNotifications(backendNotifications);
            this.setCounter(backendTotalCounter);
            this.setLoadMore(backendLoadMoreCounter > 0 ? this.callBackendLoadMore : null);
            this.setError(backendError);
            this.setPending(backendPending);
        };
        this.notifySubscribers = () => {
            Object.values(this.subscribers).forEach((callback) => {
                try {
                    callback(this.state);
                }
                catch (_a) {
                    // fail silently
                }
            });
        };
        this.notifySubscribersDebounced = debounce(this.notifySubscribers, 10);
    }
    /**
     * Counter used to inform user about the number of relevant notifications
     */
    get counter() {
        return this.state.counter;
    }
    setCounter(value) {
        if (value !== this.state.counter) {
            this.state = Object.assign(Object.assign({}, this.state), { counter: value });
            this.notifySubscribersDebounced();
        }
    }
    /**
     * Error if notifications could not be fetched
     */
    get error() {
        return this.state.error;
    }
    setError(value) {
        if (value !== this.state.error) {
            this.state = Object.assign(Object.assign({}, this.state), { error: value });
            this.notifySubscribersDebounced();
        }
    }
    /**
     * Notification list
     */
    get notifications() {
        return this.state.notifications;
    }
    setNotifications(value) {
        if (Array.isArray(value)) {
            this.state = Object.assign(Object.assign({}, this.state), { counter: value.length, notifications: value.sort(sortNotifications) });
            this.notifySubscribersDebounced();
        }
    }
    /**
     * Flag that indicates a running background process
     */
    get pending() {
        return this.state.pending;
    }
    setPending(value) {
        if (value !== this.state.pending) {
            this.state = Object.assign(Object.assign({}, this.state), { pending: value });
            this.notifySubscribersDebounced();
        }
    }
    addNotification(notification) {
        if (this.notifications.find((item) => item.id === notification.id) === undefined) {
            this.setNotifications([notification, ...this.notifications].sort(sortNotifications));
        }
    }
    removeNotification(notificationId) {
        if (this.notifications.find((item) => item.id === notificationId) !== undefined) {
            this.setNotifications(this.notifications.filter((item) => item.id !== notificationId));
        }
    }
    /**
     * Load more callback
     */
    get loadMore() {
        return this.state.loadMore;
    }
    setLoadMore(callback) {
        const loadMoreCallback = typeof callback === "function" ? callback : null;
        if (loadMoreCallback !== this.state.loadMore) {
            this.state = Object.assign(Object.assign({}, this.state), { loadMore: loadMoreCallback });
            this.notifySubscribersDebounced();
        }
    }
    registerBackend(scope, backend) {
        const scopedNotificationProvider = getNotificationProvider(scope);
        const subscriptionId = scopedNotificationProvider.subscribe(this.handleBackendProviderState);
        if (this.registeredBackends[scope]) {
            scopedNotificationProvider.unsubscribe(this.registeredBackends[scope].subscriptionId);
        }
        this.registeredBackends[scope] = {
            backend,
            scope,
            subscriptionId
        };
        backend.initProvider(scopedNotificationProvider);
    }
    // ------------------------------------------------------------------------
    // Subscriptions
    // ------------------------------------------------------------------------
    subscribe(callback) {
        const subscriberId = createSubscriberId();
        this.subscribers[subscriberId] = callback;
        return subscriberId;
    }
    unsubscribe(subscriberId) {
        delete this.subscribers[subscriberId];
    }
}
export const getNotificationProvider = (name) => {
    const providerName = name || NotificationProviderScope.Global;
    const ns = `${NAMESPACE}.${providerName}`;
    let provider = getRegisteredGlobal(ns) || null;
    if (provider === null) {
        provider = new NotificationProvider();
        registerGlobal(provider, ns);
    }
    return provider;
};
//# sourceMappingURL=provider.js.map
